import axiosInstance from '../api/axiosInstance';
import { buildQueryString } from '../utils/query';
import { NOTIFICATION_ENDPOINTS } from '@/api/endpoints/notification.endpoints';

export interface NotificationItem {
  id: number;
  type: string;
  is_read: boolean;
  title: string | null;
  body: string | null;
  link: string | null;
  related_object_type: string | null;
  related_object_id: number | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  total: number;
  unread_count: number;
}

export const NotificationApi = {
  list: async (
    opts: { limit?: number; offset?: number; unread_only?: boolean } = {}
  ): Promise<NotificationListResponse> => {
    const query = buildQueryString({
      limit: opts.limit ?? 20,
      offset: opts.offset ?? 0,
      unread_only: opts.unread_only ? true : undefined,
    });
    return (
      await axiosInstance.get<NotificationListResponse>(
        `${NOTIFICATION_ENDPOINTS.LIST}${query}`
      )
    ).data;
  },

  unreadCount: async (): Promise<number> =>
    (await axiosInstance.get<{ count: number }>(NOTIFICATION_ENDPOINTS.UNREAD_COUNT)).data
      .count,

  markRead: async (id: number): Promise<void> => {
    await axiosInstance.post(NOTIFICATION_ENDPOINTS.MARK_READ(id));
    notifyChanged();
  },

  markAllRead: async (): Promise<void> => {
    await axiosInstance.post(NOTIFICATION_ENDPOINTS.MARK_ALL_READ);
    notifyChanged();
  },
};

// ── Admin: centralized notification templates ───────────────────────────────
export interface TemplateTranslation {
  subject: string;
  body_html: string;
}

export interface NotificationTemplate {
  event_key: string;
  label: string;
  category: string;
  description: string;
  placeholders: Record<string, string>;
  subject: string;
  body_html: string;
  default_subject: string;
  default_body_html: string;
  email_enabled: boolean;
  inapp_enabled: boolean;
  is_customized: boolean;
  cc_manager: boolean;
  /** 'seminar' | 'course' | 'tenant' | 'default' — where the effective content came from. */
  scope: string;
  translations: Record<string, TemplateTranslation>;
}

export interface NotificationTemplateUpdate {
  subject: string | null;
  body_html: string | null;
  email_enabled: boolean;
  inapp_enabled: boolean;
  cc_manager: boolean;
  translations: Record<string, TemplateTranslation>;
}

export const NotificationTemplateApi = {
  list: async (): Promise<NotificationTemplate[]> =>
    (await axiosInstance.get<NotificationTemplate[]>(NOTIFICATION_ENDPOINTS.TEMPLATES)).data,

  update: async (
    key: string,
    data: NotificationTemplateUpdate
  ): Promise<NotificationTemplate> =>
    (await axiosInstance.put<NotificationTemplate>(NOTIFICATION_ENDPOINTS.TEMPLATE(key), data))
      .data,

  reset: async (key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.post<NotificationTemplate>(NOTIFICATION_ENDPOINTS.TEMPLATE_RESET(key)))
      .data,

  // ── Course-scoped overrides ──
  getCourse: async (courseId: number, key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.get<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_COURSE(courseId, key)
    )).data,

  updateCourse: async (
    courseId: number,
    key: string,
    data: NotificationTemplateUpdate
  ): Promise<NotificationTemplate> =>
    (await axiosInstance.put<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_COURSE(courseId, key),
      data
    )).data,

  resetCourse: async (courseId: number, key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.post<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_COURSE_RESET(courseId, key)
    )).data,

  // ── Program-scoped overrides (scope 'program' = this program's own version) ──
  listProgram: async (pathId: number): Promise<NotificationTemplate[]> =>
    (await axiosInstance.get<NotificationTemplate[]>(
      NOTIFICATION_ENDPOINTS.TEMPLATES_PROGRAM(pathId)
    )).data,

  updateProgram: async (
    pathId: number,
    key: string,
    data: NotificationTemplateUpdate
  ): Promise<NotificationTemplate> =>
    (await axiosInstance.put<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_PROGRAM(pathId, key),
      data
    )).data,

  resetProgram: async (pathId: number, key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.post<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_PROGRAM_RESET(pathId, key)
    )).data,

  // ── Seminar-scoped overrides (cseminar_* events; scope 'seminar' = this seminar's own version) ──
  listSeminar: async (seminarId: number): Promise<NotificationTemplate[]> =>
    (await axiosInstance.get<NotificationTemplate[]>(
      NOTIFICATION_ENDPOINTS.TEMPLATES_SEMINAR(seminarId)
    )).data,

  getSeminar: async (seminarId: number, key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.get<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_SEMINAR(seminarId, key)
    )).data,

  updateSeminar: async (
    seminarId: number,
    key: string,
    data: NotificationTemplateUpdate
  ): Promise<NotificationTemplate> =>
    (await axiosInstance.put<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_SEMINAR(seminarId, key),
      data
    )).data,

  resetSeminar: async (seminarId: number, key: string): Promise<NotificationTemplate> =>
    (await axiosInstance.post<NotificationTemplate>(
      NOTIFICATION_ENDPOINTS.TEMPLATE_SEMINAR_RESET(seminarId, key)
    )).data,
};

/** Cross-component sync: tells the header bell (and any listener) to refresh
 * its unread count after a read state change, without a shared store. */
export const NOTIFICATIONS_CHANGED_EVENT = 'notifications:changed';
function notifyChanged() {
  try {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
  } catch {
    /* SSR / no window — ignore */
  }
}
